// lib/layer-render-order.ts
import { getLayerWorkspaceId } from '#types/types';
import type { Layer } from '#types/types';

const hasFiniteRenderOrder = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isTopLayer = (layer: { 'top-layer'?: boolean }): boolean =>
  layer['top-layer'] === true;

const sortLayersByStackOrder = <T extends Pick<Layer, 'renderOrder'>>(layers: T[]): T[] => {
  return layers
    .map((layer, index) => ({ layer, index }))
    .sort((left, right) => {
      const leftOrder = hasFiniteRenderOrder(left.layer.renderOrder) ? left.layer.renderOrder : left.index;
      const rightOrder = hasFiniteRenderOrder(right.layer.renderOrder) ? right.layer.renderOrder : right.index;

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return left.index - right.index;
    })
    .map((entry) => entry.layer);
};

export const sortLayersByRenderOrder = <T extends Pick<Layer, 'renderOrder' | 'top-layer'>>(layers: T[]): T[] => {
  // Rendering pipeline:
  // 1) normal layers in stack order
  // 2) top-layer flagged layers in stack order
  const orderedByStack = sortLayersByStackOrder(layers);
  const normalLayers = orderedByStack.filter((layer) => !isTopLayer(layer));
  const topLayers = orderedByStack.filter((layer) => isTopLayer(layer));
  return [...normalLayers, ...topLayers];
};

export const mergeLayersForRender = <T extends Pick<Layer, 'id' | 'renderOrder' | 'top-layer'>>(
  layers: T[],
  extraLayers: T[] | undefined,
): T[] => {
  if (!extraLayers || extraLayers.length === 0) return layers;
  const extrasById = new Map(extraLayers.map((layer) => [layer.id, layer]));
  const replaced = layers.map((layer) => {
    const extra = extrasById.get(layer.id);
    if (!extra) return layer;
    extrasById.delete(layer.id);
    return extra;
  });
  return sortLayersByRenderOrder([...replaced, ...extrasById.values()]);
};

export const getNextRenderOrderForWorkspace = (
  layers: Pick<Layer, 'layerWorkspaceId' | 'renderOrder'>[],
  workspaceId: string,
): number => {
  let maxRenderOrder = -1;
  let workspaceLayerCount = 0;

  for (const layer of layers) {
    if (getLayerWorkspaceId(layer) !== workspaceId) continue;
    workspaceLayerCount += 1;
    if (hasFiniteRenderOrder(layer.renderOrder)) {
      maxRenderOrder = Math.max(maxRenderOrder, layer.renderOrder);
    }
  }

  if (maxRenderOrder >= 0) {
    return maxRenderOrder + 1;
  }
  return workspaceLayerCount;
};

export const assignMissingRenderOrder = (layers: Layer[]): Layer[] => {
  if (layers.length === 0) return layers;

  const maxByWorkspace = new Map<string, number>();
  for (const layer of layers) {
    if (!hasFiniteRenderOrder(layer.renderOrder)) continue;
    const workspaceId = getLayerWorkspaceId(layer);
    if (!workspaceId) continue;
    const currentMax = maxByWorkspace.get(workspaceId) ?? -1;
    if (layer.renderOrder > currentMax) {
      maxByWorkspace.set(workspaceId, layer.renderOrder);
    }
  }

  const nextByWorkspace = new Map<string, number>();
  const updatedLayers = [...layers];
  let hasChanges = false;

  for (let index = 0; index < layers.length; index += 1) {
    const layer = layers[index];
    if (!layer) continue;
    if (hasFiniteRenderOrder(layer.renderOrder)) continue;

    const workspaceId = getLayerWorkspaceId(layer);
    if (!workspaceId) continue;
    if (!nextByWorkspace.has(workspaceId)) {
      nextByWorkspace.set(workspaceId, (maxByWorkspace.get(workspaceId) ?? -1) + 1);
    }

    const nextRenderOrder = nextByWorkspace.get(workspaceId) ?? 0;
    nextByWorkspace.set(workspaceId, nextRenderOrder + 1);

    updatedLayers[index] = {
      ...layer,
      renderOrder: nextRenderOrder,
    };
    hasChanges = true;
  }

  return hasChanges ? updatedLayers : layers;
};

export const resequenceRenderOrderByArray = (layers: Layer[], workspaceIds: string[]): Layer[] => {
  if (layers.length === 0 || workspaceIds.length === 0) return layers;

  const targetWorkspaceIds = new Set(workspaceIds);
  const nextByWorkspace = new Map<string, number>();
  const updatedLayers = [...layers];
  let hasChanges = false;

  for (let index = 0; index < layers.length; index += 1) {
    const layer = layers[index];
    if (!layer) continue;
    const workspaceId = getLayerWorkspaceId(layer);
    if (!workspaceId || !targetWorkspaceIds.has(workspaceId)) continue;

    const nextRenderOrder = nextByWorkspace.get(workspaceId) ?? 0;
    nextByWorkspace.set(workspaceId, nextRenderOrder + 1);

    if (layer.renderOrder !== nextRenderOrder) {
      updatedLayers[index] = {
        ...layer,
        renderOrder: nextRenderOrder,
      };
      hasChanges = true;
    }
  }

  return hasChanges ? updatedLayers : layers;
};
